// packages/sdk/src/components/FeedbackModal.tsx

import React, { useEffect, useRef, useState } from 'react';
import { FormRenderer, useFormState } from './FormRenderer';
import type { FieldConfig, FeedbackPayload, AppearanceConfig } from '../types';
import { GENERAL_PRESET } from '../types';

// --- Props ---

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: FeedbackPayload) => Promise<void>;
  projectId: string;
  fields?: FieldConfig[];
  appearance?: AppearanceConfig;
  widgetType?: 'nps' | 'csat' | 'general' | 'custom';
}

// --- Component ---

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  fields = GENERAL_PRESET,
  appearance = {},
  widgetType = 'general',
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLDivElement>(null);

  const { values, handleChange, validate, reset } = useFormState(fields);

  const primaryColor = appearance.primaryColor ?? '#111827';

  // Animate in / out
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      setTimeout(() => firstFocusRef.current?.focus(), 150);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) handleClose();
    };
    if (isOpen) document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, isSubmitting]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
      setTimeout(() => {
        reset();
        setSubmitStatus('idle');
        setErrorMessage('');
      }, 100);
    }, 200);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const payload: FeedbackPayload = {
        project_id: projectId,
        fields: values,
        widget_type: widgetType,
        source: 'sdk',
        page_url: window.location.href,
        page_title: document.title,
        user_agent: navigator.userAgent,
        // Hoist common identity fields to top-level for backend compat
        user_email:
          typeof values.user_email === 'string'
            ? values.user_email
            : undefined,
        user_name:
          typeof values.user_name === 'string'
            ? values.user_name
            : undefined,
        user_identifier:
          (typeof values.user_email === 'string' && values.user_email) ||
          (typeof values.user_name === 'string' && values.user_name) ||
          'anonymous',
      };

      await onSubmit(payload);
      setSubmitStatus('success');
      setTimeout(() => handleClose(), 2000);
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to submit feedback'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) handleClose();
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ms-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '16px',
        opacity: isAnimating ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow:
            '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          transform: isAnimating ? 'scale(1)' : 'scale(0.95)',
          opacity: isAnimating ? 1 : 0,
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px',
          }}
        >
          <div ref={firstFocusRef} tabIndex={-1}>
            <h2
              id="ms-modal-title"
              style={{
                fontSize: '18px',
                fontWeight: 600,
                margin: 0,
                color: '#111827',
              }}
            >
              {appearance.welcomeTitle ?? 'Share your feedback'}
            </h2>
            {appearance.welcomeSubtitle && (
              <p
                style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  marginTop: '4px',
                }}
              >
                {appearance.welcomeSubtitle}
              </p>
            )}
          </div>

          <button
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close feedback form"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              color: '#9ca3af',
              lineHeight: 1,
              padding: '0 0 0 12px',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'color 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.currentTarget.style.color = '#111827';
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.currentTarget.style.color = '#9ca3af';
            }}
          >
            ×
          </button>
        </div>

        {/* Status: success */}
        {submitStatus === 'success' && (
          <div
            role="status"
            aria-live="polite"
            style={{
              backgroundColor: '#d1fae5',
              border: '1px solid #6ee7b7',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#065f46',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>✓</span>
            <span>
              {appearance.successMessage ??
                'Thank you! Your feedback has been submitted.'}
            </span>
          </div>
        )}

        {/* Status: error */}
        {errorMessage && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#991b1b',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>⚠</span>
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '0' }}
        >
          <FormRenderer
            fields={fields}
            values={values}
            onChange={handleChange}
            disabled={isSubmitting || submitStatus === 'success'}
            primaryColor={primaryColor}
          />

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '24px',
            }}
          >
            {/* Branding */}
            {appearance.showBranding !== false && (
              <span style={{ fontSize: '11px', color: '#d1d5db' }}>
                Powered by MetricShift
              </span>
            )}

            <div
              style={{
                display: 'flex',
                gap: '10px',
                marginLeft: 'auto',
              }}
            >
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                style={{
                  padding: '9px 18px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '9999px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.5 : 1,
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting)
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting)
                    e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting || submitStatus === 'success'}
                style={{
                  padding: '9px 18px',
                  border: 'none',
                  borderRadius: '9999px',
                  backgroundColor:
                    submitStatus === 'success' ? '#10b981' : primaryColor,
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor:
                    isSubmitting || submitStatus === 'success'
                      ? 'not-allowed'
                      : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: 'opacity 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {isSubmitting && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '13px',
                      height: '13px',
                      border: '2px solid #ffffff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'ms-spin 0.6s linear infinite',
                    }}
                  />
                )}
                {isSubmitting
                  ? 'Sending…'
                  : submitStatus === 'success'
                  ? '✓ Sent!'
                  : 'Submit'}
              </button>
            </div>
          </div>
        </form>

        <style>{`
          @keyframes ms-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};